/**
 * /dashboard/hiring/requisitions/new — Create a requisition (Phase H.8).
 *
 * POST /hiring/requisitions: HR creates any requisition; a manager
 * raises a "hire request" (a draft with themselves as hiring manager that
 * goes to HR for approval — 2026-10 persona round). Everyone else sees a
 * clean "no access" state. The server enforces the same rules.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/requisitions/new/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import {
  HrAdminGate,
  canRequestHire,
} from "@/components/hiring/hr-admin-gate";
import { useAuth } from "@/lib/auth";
import { RequisitionForm } from "@/components/hiring/requisition-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/requisitions/new",
)({
  component: NewRequisitionRoute,
});

function NewRequisitionRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const managerRequest = canRequestHire(user?.role);
  if (managerRequest) {
    return (
      <HiringModuleShell
        title="Request a Hire"
        description="Describe the role you need. HR reviews the request; once it is approved they open the role and start hiring."
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/hiring" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back to Hiring
            </Link>
          </Button>
        }
      >
        <HiringFeatureGate>
          <Card>
            <CardContent className="pt-6">
              <RequisitionForm managerRequest />
            </CardContent>
          </Card>
        </HiringFeatureGate>
      </HiringModuleShell>
    );
  }
  return (
    <HiringModuleShell
      title="New Requisition"
      description="Open a new role. Make it open + public to list it on your careers page."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back to Requisitions
          </Link>
        </Button>
      }
    >
      <HiringFeatureGate>
        <HrAdminGate
          title="New Requisition"
          description="Open a new role to hire for."
        >
          <Card>
            <CardContent className="pt-6">
              <RequisitionForm />
            </CardContent>
          </Card>
        </HrAdminGate>
      </HiringFeatureGate>
    </HiringModuleShell>
  );
}
